import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'

import { errorMessage, isApiError } from '@/api/client'
import type { RequestCodeResponse } from '@/api/types'
import { useAuth } from '@/auth/AuthProvider'
import { CopyrightFooter } from '@/components/CopyrightFooter'
import { Button, Card, Field, Input } from '@/components/ui'

type Step = 'email' | 'code'

function formatExpiry(iso: string | undefined): string | null {
  if (!iso) return null
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return null
  return when.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Email one-time-code sign in (plan §5 P0.4).
 *
 * Two steps, one address. The server answers `request-code` identically for a
 * known address, an unknown one and a deactivated one — so this screen must
 * too. It says "if that address has an account, a code is on its way" and
 * never "no such user": the sign-in form of a small firm is otherwise a
 * directory of its staff.
 *
 * There is no mail transport until P9.1, so outside production the server
 * returns the code in `debug_code`. It is shown, labelled as a development
 * convenience, because the alternative is reading it out of the server log.
 */
export function SignIn() {
  const { requestCode, verify, isRequestingCode, isVerifying } = useAuth()

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [issued, setIssued] = useState<RequestCodeResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const codeInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus()
  }, [step])

  const send = useCallback(
    async (address: string, { resend = false }: { resend?: boolean } = {}) => {
      setError(null)
      setFieldError(null)
      try {
        const result = await requestCode(address)
        setIssued(result)
        setStep('code')
        setNotice(
          resend
            ? 'A new code is on its way. The previous one no longer works.'
            : `If ${address} has an account, a sign-in code is on its way.`,
        )
      } catch (caught) {
        if (isApiError(caught) && caught.isValidationError) {
          setFieldError(caught.issueFor('email') ?? caught.message)
          return
        }
        setError(errorMessage(caught))
      }
    },
    [requestCode],
  )

  const onSubmitEmail = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      const address = email.trim()
      if (!address) {
        setFieldError('Enter your work email address.')
        return
      }
      void send(address)
    },
    [email, send],
  )

  const onSubmitCode = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setError(null)
      setFieldError(null)
      const entered = code.trim()
      if (!entered) {
        setFieldError('Enter the code from your email.')
        return
      }
      try {
        await verify(email.trim(), entered)
        // Nothing to do on success: `useAuth` refetches `/api/auth/me` and the
        // shell swaps this screen out.
      } catch (caught) {
        setCode('')
        codeInputRef.current?.focus()
        if (isApiError(caught) && caught.isValidationError) {
          setFieldError(caught.issueFor('code') ?? caught.message)
          return
        }
        setError(errorMessage(caught))
      }
    },
    [code, email, verify],
  )

  const startOver = useCallback(() => {
    setStep('email')
    setCode('')
    setIssued(null)
    setError(null)
    setFieldError(null)
    setNotice(null)
  }, [])

  const expiresAt = formatExpiry(issued?.expires_at)

  return (
    <div className="flex min-h-full items-center justify-center bg-ground px-4 py-16">
      <div className="w-full max-w-[420px]">
        <div className="mb-6 text-center">
          <div className="text-[18px] font-semibold tracking-[-0.01em]">TM-Traverse</div>
          <p className="m-0 mt-1 text-body text-muted">
            Provisional refusals into assessment packs and filed responses.
          </p>
        </div>

        <Card className="gap-4">
          {step === 'email' ? (
            <form onSubmit={onSubmitEmail} className="flex flex-col gap-4" noValidate>
              <div>
                <h3 className="m-0">Sign in</h3>
                <p className="m-0 mt-1 text-body text-muted">
                  We email a one-time code. There is no password to forget.
                </p>
              </div>

              <Field label="Work email" error={fieldError ?? undefined} required>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    type="email"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="name@firm.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>

              {error ? <ErrorNote>{error}</ErrorNote> : null}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={isRequestingCode}
                loadingLabel="Sending code…"
              >
                Send code
              </Button>
            </form>
          ) : (
            <form onSubmit={onSubmitCode} className="flex flex-col gap-4" noValidate>
              <div>
                <h3 className="m-0">Enter your code</h3>
                <p className="m-0 mt-1 text-body text-muted">
                  {notice ?? `If ${email} has an account, a sign-in code is on its way.`}
                  {expiresAt ? ` It expires at ${expiresAt}.` : null}
                </p>
              </div>

              <Field
                label="Sign-in code"
                error={fieldError ?? undefined}
                hint="Six digits, from the email we just sent."
                required
              >
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    ref={codeInputRef}
                    aria-describedby={describedBy}
                    invalid={invalid}
                    name="one-time-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    mono
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                  />
                )}
              </Field>

              {issued?.debug_code ? (
                <div className="rounded-md border border-warn-line bg-warn-bg px-3 py-2 text-micro text-warn-fg">
                  <strong className="font-semibold">Development only.</strong> This environment
                  sends no email, so the code is shown here:{' '}
                  <code className="font-mono">{issued.debug_code}</code>
                </div>
              ) : null}

              {error ? <ErrorNote>{error}</ErrorNote> : null}

              <Button
                type="submit"
                variant="primary"
                className="w-full"
                loading={isVerifying}
                loadingLabel="Checking…"
              >
                Sign in
              </Button>

              <div className="flex items-center justify-between text-meta">
                <button
                  type="button"
                  className="btn btn-ghost px-0"
                  onClick={startOver}
                  disabled={isVerifying}
                >
                  Use a different address
                </button>
                <button
                  type="button"
                  className="btn btn-ghost px-0"
                  onClick={() => void send(email.trim(), { resend: true })}
                  disabled={isRequestingCode || isVerifying}
                >
                  {isRequestingCode ? 'Sending…' : 'Send a new code'}
                </button>
              </div>
            </form>
          )}
        </Card>

        <p className="mt-4 text-center text-micro text-muted">
          Access is by invitation. An administrator adds accounts under Admin → Users &amp; access.
        </p>

        <CopyrightFooter />
      </div>
    </div>
  )
}

function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="rounded-md border border-block-line bg-block-bg px-3 py-2 text-body text-block-fg"
    >
      {children}
    </div>
  )
}
